import React, { useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";
import { LoLang } from "maven-lib/dist/consts";
import { useCaptureContext } from "@layouts/ecoa/CaptureContext";
import { VisitQuestionnaireAnswer } from "@pages/ecoa/visit/VisitQuestionnaire";
import { CrfTimeItemSpec, CrfTimeItemSpecTimeExpr, InitialItemSequence } from "maven-lib/dist/consts/edc";
import { getLabel, removeDbPrefixAndSuffix } from "maven-lib/dist/consts/edc/lib";
import { LOCALE_LANG_KEY } from "maven-lib/dist/states";
import { CrDropdown } from "@maven-surface/components/base/CrDropdown";
import { ECOA_CAPTURE_LANG } from "@maven-msg/lang/ecoaCapture";
import { MvnMessage } from "@maven-msg/lib/Message";

interface StyledTimeWrapperProps {
  textAlignment?: "LEFT" | "CENTER";
}

const StyledTimeWrapper = styled.div<StyledTimeWrapperProps>`
  & .cr-dropdown {
    text-align: ${(props) => props.textAlignment?.toLowerCase() || "left"};
  }
`;

const StyledTimeDelimiter = styled.span`
  margin: 0 0.2em;
  word-break: keep-all;
`;

const HOUR_COUNT_12 = 12;
const HOUR_COUNT_24 = 24;
const MINUTE_COUNT_60 = 60;

enum TimeOfDay {
  Am = "AM",
  Pm = "PM",
}

type TimeType = {
  hour: string | number | null;
  minute: string | number | null;
};

interface QuestionnaireTimeItemProps {
  item: CrfTimeItemSpec;
  disabled?: boolean;
  isAvailable?: boolean;
  defaultItemValues?: Array<{ itemId: string | null; dbVal: string | number | null }>;
  onValueChange: (values: Array<VisitQuestionnaireAnswer>) => void;
  prefix?: string;
  suffix?: string;
}

export function QuestionnaireTimeItem({
  isAvailable = true,
  item,
  defaultItemValues,
  disabled,
  onValueChange,
  prefix,
  suffix,
}: QuestionnaireTimeItemProps) {
  const { captures } = useCaptureContext();
  const localStorageLanguage = localStorage.getItem(LOCALE_LANG_KEY) as LoLang;

  const { id: itemId, delimiter, timeExpression, dbPrefix, dbSuffix, resetCaptureItemIfChanged, itemSeq, unknowable } = item;

  const timeFormat = typeof timeExpression === "string" ? timeExpression : timeExpression?.format;

  const [amPm, setAmPm] = useState<TimeOfDay>(TimeOfDay.Am);
  const [time, setTime] = useState<TimeType>({
    hour: null,
    minute: null,
  });

  const hourItems = useMemo(() => generateHoursOptions(amPm, timeExpression, unknowable?.hour), [amPm, timeExpression, unknowable?.hour]);
  const minuteItems = useMemo(() => generateMinutesOptions(timeExpression, unknowable?.minute), [timeExpression, unknowable?.minute]);

  useEffect(() => {
    const timeDbVal = captures.find((e) => e.itemId === itemId)?.dbVal || null;
    const defaultTime = timeDbVal ? removeDbPrefixAndSuffix(timeDbVal, { dbPrefix, dbSuffix }) : timeDbVal;
    const { hour, minute } = parsingDefaultTime(defaultTime, delimiter);

    if (timeFormat === "12_HOUR_CLOCK") {
      const timeOfDay = (() => {
        if (timeExpression && typeof timeExpression === "object" && "hour" in timeExpression && timeExpression.hour) {
          const { min, max } = timeExpression.hour;

          if (min > HOUR_COUNT_12 - 1) {
            return TimeOfDay.Pm;
          } else if (max < HOUR_COUNT_12) {
            return TimeOfDay.Am;
          }
        } else {
          return Number(hour) >= HOUR_COUNT_12 ? TimeOfDay.Pm : TimeOfDay.Am;
        }

        return TimeOfDay.Am;
      })();

      setAmPm(timeOfDay);
      setTime({
        hour: (() => {
          if (timeFormat === "12_HOUR_CLOCK") {
            if (Number(hour) > HOUR_COUNT_12) {
              return String(Number(hour) - HOUR_COUNT_12).padStart(2, "0");
            } else if (hour !== null && Number(hour) === 0) {
              return String(HOUR_COUNT_12);
            }
          }

          return hour;
        })(),
        minute: minute,
      });
    } else {
      setTime({
        hour,
        minute,
      });
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const currentCapture = captures.find((e) => e.itemId === itemId);

    if (currentCapture?.dbVal === null && (time.hour !== null || time.minute !== null)) {
      setAmPm(TimeOfDay.Am);
      setTime({
        hour: null,
        minute: null,
      });
    }
    // eslint-disable-next-line
  }, [captures]);

  useEffect(() => {
    if (!time.hour || !time.minute) {
      return;
    }

    const captureHour = (() => {
      if (timeFormat === "12_HOUR_CLOCK") {
        if (amPm === TimeOfDay.Pm && Number(time.hour) !== HOUR_COUNT_12) {
          return String(Number(time.hour) + HOUR_COUNT_12).padStart(2, "0");
        }
        if (amPm === TimeOfDay.Am && Number(time.hour) === HOUR_COUNT_12) {
          return "00";
        }
      }

      return time.hour;
    })();

    const captureMinute = time.minute;
    const dbVal = `${captureHour}${delimiter ?? ""}${captureMinute}`;

    const onValueChangeData: Array<VisitQuestionnaireAnswer> = [{ itemId, dbVal }];

    const currentCapture = captures.find((e) => e.itemId === itemId);

    if (resetCaptureItemIfChanged && currentCapture && currentCapture.dbVal !== dbVal) {
      const sectionItems = resetCaptureItemIfChanged;

      for (const sectionItem of sectionItems) {
        const capture = captures.find(
          (e) => e.itemId === sectionItem.id && (sectionItem.sameSeq ? e.itemSeq === itemSeq : e.itemSeq === InitialItemSequence)
        );

        if (!capture || capture.itemId === null) continue;

        onValueChangeData.push({ itemId: capture.itemId, dbVal: null });
      }
    }

    onValueChange(onValueChangeData);
    // eslint-disable-next-line
  }, [time]);

  useEffect(() => {
    if (defaultItemValues && !isAvailable && (time.hour !== null || time.minute !== null) && !item.ecoa?.noSkipWhenDisabled) {
      onValueChange([{ itemId, dbVal: null }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultItemValues, isAvailable, itemId, onValueChange, time]);

  const handleChangeAmPm = (newValue: string | number | null) => {
    setAmPm(newValue === TimeOfDay.Am ? TimeOfDay.Am : TimeOfDay.Pm);
    setTime({ hour: null, minute: null });

    const onValueChangeData: Array<VisitQuestionnaireAnswer> = [{ itemId, dbVal: null }];

    const currentCapture = captures.find((e) => e.itemId === itemId);

    if (resetCaptureItemIfChanged && currentCapture && currentCapture.dbVal !== null) {
      const sectionItems = resetCaptureItemIfChanged;

      for (const sectionItem of sectionItems) {
        const capture = captures.find(
          (e) => e.itemId === sectionItem.id && (sectionItem.sameSeq ? e.itemSeq === itemSeq : e.itemSeq === InitialItemSequence)
        );

        if (!capture || capture.itemId === null) continue;

        onValueChangeData.push({ itemId: capture.itemId, dbVal: null });
      }
    }

    onValueChange(onValueChangeData);
  };

  const handleChangeHour = (newValue: string | number | null) => {
    setTime((prevTime) => ({ ...prevTime, hour: newValue }));
  };

  const handleChangeMinute = (newValue: string | number | null) => {
    setTime((prevTime) => ({ ...prevTime, minute: newValue }));
  };

  return (
    <StyledTimeWrapper className="app-ecoa-time-wrapper" textAlignment={item?.textAlignment}>
      <div className="--item-time">
        {!!prefix && (
          <div className={"--prefix"}>
            <span>{prefix}</span>
          </div>
        )}
        {!!timeFormat && timeFormat === "12_HOUR_CLOCK" ? (
          <CrDropdown
            placeholder={MvnMessage(ECOA_CAPTURE_LANG.ECOA_CAPTURE_PLACEHOLDER_TIME_FORMAT_AM)}
            key={"timeFormat"}
            disabled={disabled}
            selectedKey={amPm}
            onValueChange={handleChangeAmPm}
            items={generateTimeOfDayOptions(timeExpression)}
          />
        ) : null}

        <CrDropdown
          placeholder={
            item.placeholder?.hour
              ? getLabel(item.placeholder.hour, localStorageLanguage === LoLang.EN ? "en" : "ko")
              : MvnMessage(ECOA_CAPTURE_LANG.ECOA_CAPTURE_PLACEHOLDER_HOUR)
          }
          key={"hour"}
          selectedKey={time.hour}
          disabled={disabled}
          onValueChange={handleChangeHour}
          items={hourItems}
        />

        {delimiter && <StyledTimeDelimiter>{delimiter}</StyledTimeDelimiter>}

        <CrDropdown
          placeholder={
            item.placeholder?.minute
              ? getLabel(item.placeholder.minute, localStorageLanguage === LoLang.EN ? "en" : "ko")
              : MvnMessage(ECOA_CAPTURE_LANG.ECOA_CAPTURE_PLACEHOLDER_MIN)
          }
          key={"minute"}
          selectedKey={time.minute}
          disabled={disabled}
          items={minuteItems}
          onValueChange={handleChangeMinute}
        />
        {!!suffix && (
          <div className={"--suffix"}>
            <span>{suffix}</span>
          </div>
        )}
      </div>
    </StyledTimeWrapper>
  );
}

type GenerateTimeItemFormat = { key: string; text: string };

const convertTimeItemFormat = (num: number): GenerateTimeItemFormat => ({
  key: String(num).padStart(2, "0"),
  text: String(num).padStart(2, "0"),
});

function generateTimeOfDayOptions(timeExpression?: CrfTimeItemSpecTimeExpr): Array<GenerateTimeItemFormat> {
  const timeOfDayOptions: Array<GenerateTimeItemFormat> = [
    {
      key: TimeOfDay.Am,
      text: MvnMessage(ECOA_CAPTURE_LANG.ECOA_CAPTURE_PLACEHOLDER_TIME_FORMAT_AM),
    },
    {
      key: TimeOfDay.Pm,
      text: MvnMessage(ECOA_CAPTURE_LANG.ECOA_CAPTURE_PLACEHOLDER_TIME_FORMAT_PM),
    },
  ];

  return timeOfDayOptions.filter((e) => {
    if (typeof timeExpression === "string" ? timeExpression === "24_HOUR_CLOCK" : timeExpression?.format === "24_HOUR_CLOCK") {
      return false;
    } else if (timeExpression && typeof timeExpression === "object" && "hour" in timeExpression && timeExpression.hour) {
      const { min, max } = timeExpression.hour;

      if (min > HOUR_COUNT_12 - 1 && e.key !== TimeOfDay.Pm) {
        return false;
      } else if (max < HOUR_COUNT_12 && e.key !== TimeOfDay.Am) {
        return false;
      }
    }

    return true;
  });
}

function generateHoursOptions(
  amPm: TimeOfDay,
  timeExpression?: CrfTimeItemSpecTimeExpr,
  unknowable?: boolean
): Array<GenerateTimeItemFormat> {
  const hourArray = (() => {
    if (timeExpression && typeof timeExpression === "object" && "hour" in timeExpression && timeExpression.hour) {
      const { min, max } = timeExpression.hour;

      const hours = Array.from({ length: max - min + 1 }, (_, idx) => idx + min);

      if (timeExpression.format === "12_HOUR_CLOCK") {
        if (amPm === TimeOfDay.Am) {
          return hours.filter((e) => e < HOUR_COUNT_12).map((e) => convertTimeItemFormat(e % HOUR_COUNT_12 === 0 ? HOUR_COUNT_12 : e));
        } else {
          return hours
            .filter((e) => e >= HOUR_COUNT_12)
            .map((e) => convertTimeItemFormat(e % HOUR_COUNT_12 === 0 ? HOUR_COUNT_12 : e - HOUR_COUNT_12));
        }
      }

      return hours.map((e) => convertTimeItemFormat(e));
    } else {
      const generateCount = timeExpression === "12_HOUR_CLOCK" ? HOUR_COUNT_12 : HOUR_COUNT_24;

      return Array.from({ length: generateCount }, (_, idx) =>
        convertTimeItemFormat(timeExpression === "12_HOUR_CLOCK" && idx % HOUR_COUNT_12 === 0 ? HOUR_COUNT_12 : idx)
      );
    }
  })();

  unknowable && hourArray.push({ key: "UK", text: "UK" });

  return hourArray;
}

function generateMinutesOptions(timeExpression?: CrfTimeItemSpecTimeExpr, unknowable?: boolean): Array<GenerateTimeItemFormat> {
  const minuteArray = (() => {
    if (timeExpression && typeof timeExpression === "object" && "minute" in timeExpression && timeExpression.minute) {
      const { min, max, step } = timeExpression.minute;

      const minutes = Array.from({ length: max - min + 1 }, (_, idx) => min + idx);

      if (step) {
        return minutes.filter((e) => e % step === 0).map((e) => convertTimeItemFormat(e));
      }

      return minutes.map((e) => convertTimeItemFormat(e));
    } else {
      const generateCount = MINUTE_COUNT_60;

      return Array.from({ length: generateCount }, (_, idx) => convertTimeItemFormat(idx));
    }
  })();

  unknowable && minuteArray.push({ key: "UK", text: "UK" });

  return minuteArray;
}

function parsingDefaultTime(defaultTime: string | number | null, delimiter: string | undefined): TimeType {
  if (!defaultTime) {
    return {
      hour: null,
      minute: null,
    };
  }
  const time = String(defaultTime);

  if (delimiter) {
    const [hour, minute] = time.split(delimiter);

    return { hour, minute };
  } else {
    return { hour: time.substring(0, 2), minute: time.substring(2) };
  }
}
